import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { DeleteConfirmDialogProps } from '../AssetExplorer.types';

export const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
    open,
    assetCount,
    assetNames,
    folderCount = 0,
    folderNames = [],
    onConfirm,
    onCancel,
    loading,
}) => {
    const theme = useTheme();

    const fc = folderCount;
    const totalItems = assetCount + fc;

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onCancel}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 },
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningAmberIcon color="warning" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Confirm Delete
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Are you sure you want to delete{' '}
                    {assetCount > 0 && fc > 0 && (
                        <>
                            <strong>
                                {assetCount} asset{assetCount !== 1 ? 's' : ''}
                            </strong>{' '}
                            and{' '}
                            <strong>
                                {fc} folder{fc !== 1 ? 's' : ''}
                            </strong>
                        </>
                    )}
                    {assetCount > 0 && fc === 0 && (
                        <strong>
                            {assetCount} asset{assetCount !== 1 ? 's' : ''}
                        </strong>
                    )}
                    {assetCount === 0 && fc > 0 && (
                        <strong>
                            {fc} folder{fc !== 1 ? 's' : ''}
                        </strong>
                    )}
                    ?
                </Typography>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    This cannot be undone. Folders are removed with all nested content.
                </Typography>

                {folderNames.length > 0 && (
                    <>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Folders
                        </Typography>
                        <Box
                            sx={{
                                maxHeight: 140,
                                overflow: 'auto',
                                backgroundColor: alpha(theme.palette.warning.main, 0.06),
                                borderRadius: 1,
                                border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                                mb: 2,
                            }}
                        >
                            <List dense>
                                {folderNames.slice(0, 10).map((name, index) => (
                                    <ListItem key={`f-${index}`} sx={{ py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <FolderOutlinedIcon fontSize="small" color="warning" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={name}
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                noWrap: true,
                                            }}
                                        />
                                    </ListItem>
                                ))}
                                {folderNames.length > 10 && (
                                    <ListItem sx={{ py: 0.5 }}>
                                        <ListItemText
                                            primary={`... and ${folderNames.length - 10} more`}
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                color: 'textSecondary',
                                                fontStyle: 'italic',
                                            }}
                                            sx={{ pl: 4.5 }}
                                        />
                                    </ListItem>
                                )}
                            </List>
                        </Box>
                    </>
                )}

                {assetNames.length > 0 && (
                    <>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Assets
                        </Typography>
                        <Box
                            sx={{
                                maxHeight: 200,
                                overflow: 'auto',
                                backgroundColor: alpha(theme.palette.error.main, 0.04),
                                borderRadius: 1,
                                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                            }}
                        >
                            <List dense>
                                {assetNames.slice(0, 10).map((name, index) => (
                                    <ListItem key={`a-${index}`} sx={{ py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <InsertDriveFileIcon fontSize="small" color="action" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={name}
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                noWrap: true,
                                            }}
                                        />
                                    </ListItem>
                                ))}
                                {assetNames.length > 10 && (
                                    <ListItem sx={{ py: 0.5 }}>
                                        <ListItemText
                                            primary={`... and ${assetNames.length - 10} more`}
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                color: 'textSecondary',
                                                fontStyle: 'italic',
                                            }}
                                            sx={{ pl: 4.5 }}
                                        />
                                    </ListItem>
                                )}
                            </List>
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button
                    onClick={onCancel}
                    disabled={loading}
                    variant="outlined"
                    color="secondary"
                    sx={{ borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={onConfirm}
                    disabled={loading || totalItems === 0}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {loading ? 'Deleting...' : `Delete ${totalItems} item${totalItems !== 1 ? 's' : ''}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteConfirmDialog;
